import { createSelector } from 'reselect';
import type { DataState } from './data.reducer';
import type { ShgArrear, ShgAuditItem } from '../../api/httpTismsApi';
import type {
  Dccb,
  Branch,
  Atm,
  Inspection,
  CalendarMonth,
  Campaign,
  Product,
  NpaClass,
  RiskWeight,
  Area,
  PreUpload,
  PreCrar,
  RedFlag,
  LoanSample,
  GoldPacket,
  Denom,
  KycTier,
  EwsAlert,
  ItArea,
  Camelsc,
  Report,
} from '../../data/tisms-api';

/* Re-export the reference-data types so pages import them from the slice rather
   than from the mock module (keeps pages free of `data/tisms-api`). */
export type {
  Dccb,
  Branch,
  Atm,
  Inspection,
  CalendarMonth,
  Campaign,
  Product,
  NpaClass,
  RiskWeight,
  Area,
  AreaItem,
  PreUpload,
  PreCrar,
  RedFlag,
  LoanSample,
  GoldPacket,
  Denom,
  KycTier,
  EwsAlert,
  ItArea,
  Camelsc,
  Report,
  Grade,
  Severity,
  Observation,
  Role,
  RoleId,
} from '../../data/tisms-api';
export type { ShgArrear, ShgAuditItem } from '../../api/httpTismsApi';

const selectData = (state: { data: DataState }): DataState => state.data;

/* ---- load-status flags ---- */
export const selectDataLoaded = createSelector([selectData], (d) => d.loaded);
export const selectDataLoading = createSelector([selectData], (d) => d.loading);
export const selectDataError = createSelector([selectData], (d) => d.error);

/* ---- standalone list endpoints ---- */
export const selectDccbs = createSelector([selectData], (d): Dccb[] => d.dccbs);
export const selectBranches = createSelector([selectData], (d): Branch[] => d.branches);
export const selectAtms = createSelector([selectData], (d): Atm[] => d.bootstrap?.atms ?? []);

/* ---- bootstrap-backed datasets (sensible defaults until loaded) ---- */
export const selectInspections = createSelector(
  [selectData],
  (d): Inspection[] => d.bootstrap?.inspections ?? [],
);
export const selectInspectionTypes = createSelector(
  [selectData],
  (d): { code: string; label: string }[] => d.bootstrap?.inspectionTypes ?? [],
);


export const selectProducts = createSelector(
  [selectData],
  (d): Product[] => d.bootstrap?.products ?? [],
);
export const selectNpaClasses = createSelector(
  [selectData],
  (d): NpaClass[] => d.bootstrap?.npaClasses ?? [],
);
export const selectRiskWeights = createSelector(
  [selectData],
  (d): RiskWeight[] => d.bootstrap?.riskWeights ?? [],
);
export const selectAreas = createSelector([selectData], (d): Area[] => d.bootstrap?.areas ?? []);
export const selectCbs26 = createSelector([selectData], (d): string[] => d.bootstrap?.cbs26 ?? []);


export const selectDenoms = createSelector([selectData], (d): Denom[] => d.bootstrap?.denoms ?? []);
export const selectShgAudit = createSelector(
  [selectData],
  (d): ShgAuditItem[] => d.bootstrap?.shgAudit ?? [],
);
export const selectNabardAudit = createSelector(
  [selectData],
  (d): ShgAuditItem[] => d.bootstrap?.nabardAudit ?? [],
);
export const selectCamelsc = createSelector([selectData], (d): Camelsc[] => d.bootstrap?.camelsc ?? []);
